import { Component, HostListener, OnDestroy, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Router,
  RouterLink,
  RouterLinkActive,
  NavigationEnd
} from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { TokenService } from '../token.service';
import { ROLES } from '../roles.constants';

interface NavLink {
  path: string;
  label: string;
  exact?: boolean;
  children?: NavLink[];
  requiresAuth?: boolean;
}

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './navbar.component.html',
})
export class NavbarComponent implements OnDestroy {
  isMenuOpen = false;
  openDropdown: string | null = null;
  isHome = false;
  isScrolled = false;
  profileOpen = false;
  currentUrl = '';

  private destroy$ = new Subject<void>();

  /** Cached navLinks — rebuilt only when navigation or roles change. */
  private _navLinks: NavLink[] = [];


  constructor(
    private tokenService: TokenService,
    private router: Router
  ) {
    this.currentUrl = this.router.url;
    this.isHome = this.router.url === '/';

    // Rebuild navLinks on route change
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.currentUrl = this.router.url;
        this.isHome = this.router.url === '/';
        this.buildNavLinks();
      });

    // Rebuild navLinks when roles change (login/logout)
    this.tokenService.roles$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.buildNavLinks();
      });

    // Initial build
    this.buildNavLinks();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  // ── Signals derivadas del TokenService ──
  isLoggedIn = computed(() => this.tokenService.rolesSignal().length > 0);
  userName = computed(() => {
    const user = this.tokenService.userSignal();
    if (user?.nombre || user?.apellido) {
      return `${user.nombre} ${user.apellido}`.trim();
    }
    return 'Mi cuenta';
  });
  userInitials = computed(() => {
    const user = this.tokenService.userSignal();
    const first = user?.nombre?.charAt(0) ?? '';
    const last = user?.apellido?.charAt(0) ?? '';
    return `${first}${last}`.trim() || 'U';
  });
  showAulas = computed(() => this.tokenService.rolesSignal().includes(ROLES.ADMINISTRADOR));
  showAgenda = computed(() => this.tokenService.rolesSignal().includes(ROLES.PARTICIPANTE));
  showUsuarios = computed(() =>
    this.tokenService.rolesSignal().includes(ROLES.ADMINISTRADOR) ||
    this.tokenService.rolesSignal().includes(ROLES.ORGANIZADOR_CIENTIFICO)
  );
  showInscripcionesDropdown = computed(() =>
    this.isLoggedIn() && this.tokenService.rolesSignal().includes(ROLES.ADMINISTRADOR)
  );

  @HostListener('window:scroll')
  onScroll() {
    this.isScrolled = window.scrollY > 30;
  }

  @HostListener('document:click')
  closeProfile() {
    this.profileOpen = false;
  }

  toggleProfile(event: MouseEvent): void {
    event.stopPropagation();
    this.profileOpen = !this.profileOpen;
  }

  /** Returns the stable cached navLinks array — safe for *ngFor. */
  get navLinks(): NavLink[] {
    return this._navLinks;
  }

  /** True si la ruta actual corresponde a `path` (exacta o prefijo de segmento). */
  esRutaActiva(path: string, exact = false): boolean {
    if (exact) {
      return this.currentUrl === path;
    }
    return this.currentUrl === path || this.currentUrl.startsWith(path + '/');
  }

  /** True si el desplegable tiene algún hijo en la ruta actual (resalta la opción general). */
  padreActivo(link: NavLink): boolean {
    return !!link.children?.some(child => this.esRutaActiva(child.path, child.exact));
  }

  /** Rebuild _navLinks from current auth/role state. */
  private buildNavLinks(): void {
    this._navLinks = this.computeNavLinks();
  }

  private computeNavLinks(): NavLink[] {
    const links: NavLink[] = [
      { path: '/', label: 'Inicio', exact: true },
    ];

    // Cronograma con sub-pestañas (solo si el usuario es admin ve Aulas)
    if (this.showAulas()) {
      links.push({
        path: '/cronograma',
        label: 'Cronograma',
        children: [
          { path: '/cronograma', label: 'Cronograma' },
          { path: '/aulas', label: 'Aulas' },
        ],
      });
    } else {
      links.push({ path: '/cronograma', label: 'Cronograma' });
    }

    if (this.showAgenda()) {
      links.push({ path: '/agenda', label: 'Agenda' });
    }

    const roles = this.tokenService.getRoles();

    const trabajoChildren: NavLink[] = [];
    if (roles.some(r => r === ROLES.PARTICIPANTE || r === ROLES.AUTOR)) {
      trabajoChildren.push({ path: '/trabajos/mis-trabajos', label: 'Mis trabajos' });
    }
    if (roles.some(r => r === ROLES.EVALUADOR)) {
      trabajoChildren.push({ path: '/trabajos/evaluar', label: 'Evaluar trabajos' });
    }
    if (roles.some(r => r === ROLES.ADMINISTRADOR || r === ROLES.ORGANIZADOR_CIENTIFICO)) {
      trabajoChildren.push({ path: '/trabajos/ver-trabajos', label: 'Asignar trabajos' });
    }

    links.push({
      path: '/trabajos',
      label: 'Trabajos',
      children: trabajoChildren.length > 0 ? trabajoChildren : undefined,
    });

    const esAdmin = roles.includes(ROLES.ADMINISTRADOR);
    const esParticipante = roles.includes(ROLES.PARTICIPANTE);

    if (esAdmin && esParticipante) {
      links.push({
        path: '/inscripciones',
        label: 'Inscripciones',
        children: [
          { path: '/mi-inscripcion', label: 'Mi Inscripción' },
          { path: '/inscripciones', label: 'Inscripciones' }
        ]
      });
    } else if (esAdmin) {
      links.push({ path: '/inscripciones', label: 'Inscripciones' });
    } else if (esParticipante) {
      links.push({ path: '/mi-inscripcion', label: 'Inscripción' });
    }

    links.push({ path: '/mapa', label: 'Mapa' });

    // Novedades con sub-pestañas (solo admin ve Gestión)
    if (this.showAulas()) {
      links.push({
        path: '/novedades',
        label: 'Novedades',
        children: [
          { path: '/novedades', label: 'Publicaciones' },
          { path: '/gestion/novedades', label: 'Gestión' },
        ],
      });
    } else {
      links.push({ path: '/novedades', label: 'Novedades' });
    }

    // Configuración agrupada como dropdown: Congreso + Usuarios + Notificaciones
    const configuracionChildren: NavLink[] = [];
    if (this.showAulas()) {
      configuracionChildren.push({ path: '/configuracion', label: 'Congreso' });
      configuracionChildren.push({ path: '/admin/notificaciones', label: 'Notificaciones' });
    }
    if (this.showUsuarios()) {
      configuracionChildren.push({ path: '/usuarios', label: 'Usuarios' });
    }

    if (configuracionChildren.length > 0) {
      links.push({
        path: '/configuracion',
        label: 'Configuración',
        children: configuracionChildren,
      });
    }

    return links;
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
    if (!this.isMenuOpen) {
      this.openDropdown = null;
    }
  }

  closeMenu(): void {
    this.isMenuOpen = false;
    this.openDropdown = null;
  }

  toggleDropdown(label: string): void {
    this.openDropdown = this.openDropdown === label ? null : label;
  }

  logout(): void {
    this.tokenService.clearToken();
    this.tokenService.clearRoles();
    this.tokenService.clearUser();
    this.router.navigate(['/login']);
  }
}
